import React from 'react';
import { Box, Typography, Tooltip, IconButton, CircularProgress } from '@mui/material';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import CheckIcon from '@mui/icons-material/Check';

// Build a plain-language tooltip summary
const buildTooltip = (emp) => {
  const lines = [];
  if (!emp.hasPreferences) lines.push('This employee has not set any work preferences.');
  const { skills, preferences, hours, distance } = emp.breakdown;
  if (skills) {
    if (skills.status === 'full') lines.push(`✓ Has all required skills (${skills.matched}/${skills.total})`);
    else if (skills.status === 'partial') lines.push(`~ Has some required skills (${skills.matched}/${skills.total})`);
    else if (skills.status === 'neutral') lines.push('— Skills not specified by employee');
    else lines.push('✗ Missing required skills');
  }
  if (preferences) {
    if (preferences.status === 'full') lines.push(`✓ Meets all work preferences (${preferences.matched}/${preferences.total})`);
    else if (preferences.status === 'partial') lines.push(`~ Meets some work preferences (${preferences.matched}/${preferences.total})`);
    else if (preferences.status === 'neutral') lines.push('— Work preferences not specified');
    else lines.push('✗ Does not meet work preferences');
  }
  if (hours) {
    const map = {
      full: '✓ Available during this shift time',
      partial: '~ Partially available during this shift',
      neutral: '— Availability not specified',
      none: '✗ Not available during this shift time',
    };
    lines.push(map[hours.status] || '— Availability unknown');
  }
  if (distance) {
    const map = {
      full: '✓ Lives nearby',
      partial: '~ Reasonable travel distance',
      neutral: '— Location not on file',
      none: '✗ May be too far away',
    };
    lines.push(map[distance.status] || '— Location unknown');
  }
  return lines.join('\n');
};

const matchColor = (pct) => {
  if (pct >= 80) return '#4caf50';
  if (pct >= 50) return '#ff9800';
  return '#ef5350';
};

const matchBg = (pct) => {
  if (pct >= 80) return '#e8f5e9';
  if (pct >= 50) return '#fff3e0';
  return '#ffebee';
};

function SuggestedEmployees({ suggestions, loading, onSelect, selectedIds, visible }) {
  if (!visible) return null;

  return (
    <Box sx={{ mt: 1, mb: 1 }}>
      <Typography variant="subtitle2" sx={{ mb: 0.5, color: 'text.secondary' }}>
        Available Employees
      </Typography>
      {loading ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1.5 }}>
          <CircularProgress size={16} />
          <Typography variant="body2" color="text.secondary">Looking for available employees...</Typography>
        </Box>
      ) : (!suggestions || suggestions.length === 0) ? (
        <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>No employees matched the selected criteria.</Typography>
      ) : (
      <Box sx={{ maxHeight: 200, overflowY: 'auto', border: '1px solid #e0e0e0', borderRadius: 1 }}>
        {suggestions.map((emp) => {
          const isSelected = selectedIds.includes(emp.employee_id);
          return (
            <Tooltip
              key={emp.employee_id}
              title={<span style={{ whiteSpace: 'pre-line', fontSize: '0.8rem' }}>{buildTooltip(emp)}</span>}
              placement="left"
              arrow
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  px: 1.5,
                  py: 0.75,
                  borderBottom: '1px solid #f0f0f0',
                  bgcolor: isSelected ? '#e3f2fd' : 'transparent',
                  '&:hover': { bgcolor: isSelected ? '#e3f2fd' : '#fafafa' },
                  '&:last-child': { borderBottom: 'none' },
                  cursor: 'default',
                }}
              >
                {/* Name */}
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 500, flex: 1, minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                >
                  {emp.employee_name}
                </Typography>

                {/* Fit label */}
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 600,
                    px: 1,
                    py: 0.25,
                    borderRadius: 1,
                    bgcolor: matchBg(emp.matchPercent),
                    color: matchColor(emp.matchPercent),
                    whiteSpace: 'nowrap',
                  }}
                >
                  {emp.matchPercent >= 80 ? 'Good fit' : emp.matchPercent >= 50 ? 'Fair fit' : 'Low fit'}
                </Typography>

                {/* Add / Already added */}
                <IconButton
                  size="small"
                  onClick={() => !isSelected && onSelect(emp)}
                  disabled={isSelected}
                  sx={{ p: 0.25 }}
                >
                  {isSelected
                    ? <CheckIcon sx={{ fontSize: 18, color: '#4caf50' }} />
                    : <PersonAddIcon sx={{ fontSize: 18, color: '#1976d2' }} />
                  }
                </IconButton>
              </Box>
            </Tooltip>
          );
        })}
      </Box>
      )}
    </Box>
  );
}

export default SuggestedEmployees;
